import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    // SDK list
    sdkList: [],
    total: 0,
    page: 1,
    pageSize: 10,

    // SDK logs
    logs: [],
    logsTotal: 0,
    logsPage: 1,
    logsPageSize: 10,
    logsFilters: {
        unitId: '',
        fromDate: null,
        toDate: null,
    },

    // Init data
    initData: {
        methods: [],
        securities: [],
        statuses: [],
    },

    // Loading states
    loading: false,
    listLoading: false,
    logsLoading: false,
    createLoading: false,
    updateLoading: false,
    deleteLoading: false,

    // Messages
    error: null,
    successMessage: null,
};

const vneidSdkSlice = createSlice({
    name: 'vneidSdk',
    initialState,
    reducers: {
        // Fetch SDK List
        fetchSdkList: (state) => {
            state.listLoading = true;
            state.error = null;
        },
        fetchSdkListSuccess: (state, action) => {
            state.listLoading = false;
            state.sdkList = action.payload.data || [];
            state.total = action.payload.meta?.total || 0;
            state.error = null;
        },
        fetchSdkListFailure: (state, action) => {
            state.listLoading = false;
            state.error = action.payload;
        },

        // Fetch Init Data
        fetchInitData: (state) => {
            state.loading = true;
        },
        fetchInitDataSuccess: (state, action) => {
            state.loading = false;
            state.initData = action.payload;
        },
        fetchInitDataFailure: (state, action) => {
            state.loading = false;
            state.error = action.payload;
        },

        // Create SDK
        createSdk: (state) => {
            state.createLoading = true;
            state.error = null;
            state.successMessage = null;
        },
        createSdkSuccess: (state, action) => {
            state.createLoading = false;
            state.successMessage = action.payload;
            state.error = null;
        },
        createSdkFailure: (state, action) => {
            state.createLoading = false;
            state.error = action.payload;
        },

        // Update SDK
        updateSdk: (state) => {
            state.updateLoading = true;
            state.error = null;
            state.successMessage = null;
        },
        updateSdkSuccess: (state, action) => {
            state.updateLoading = false;
            state.successMessage = action.payload;
            state.error = null;
        },
        updateSdkFailure: (state, action) => {
            state.updateLoading = false;
            state.error = action.payload;
        },

        // Delete SDK
        deleteSdk: (state) => {
            state.deleteLoading = true;
            state.error = null;
            state.successMessage = null;
        },
        deleteSdkSuccess: (state, action) => {
            state.deleteLoading = false;
            state.successMessage = action.payload;
            state.error = null;
        },
        deleteSdkFailure: (state, action) => {
            state.deleteLoading = false;
            state.error = action.payload;
        },

        // Pagination
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setPageSize: (state, action) => {
            state.pageSize = action.payload;
            state.page = 1;
        },

        // Fetch SDK Logs
        fetchSdkLogs: (state) => {
            state.logsLoading = true;
            state.error = null;
        },
        fetchSdkLogsSuccess: (state, action) => {
            state.logsLoading = false;
            state.logs = action.payload.data || [];
            state.logsTotal = action.payload.meta?.total || 0;
            state.error = null;
        },
        fetchSdkLogsFailure: (state, action) => {
            state.logsLoading = false;
            state.error = action.payload;
        },

        // Logs Pagination
        setLogsPage: (state, action) => {
            state.logsPage = action.payload;
        },
        setLogsPageSize: (state, action) => {
            state.logsPageSize = action.payload;
            state.logsPage = 1;
        },

        // Logs Filters
        setLogsFilters: (state, action) => {
            state.logsFilters = { ...state.logsFilters, ...action.payload };
            state.logsPage = 1;
        },
        clearLogsFilters: (state) => {
            state.logsFilters = initialState.logsFilters;
            state.logsPage = 1;
        },

        // Clear messages
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
    },
});

export const {
    fetchSdkList,
    fetchSdkListSuccess,
    fetchSdkListFailure,
    fetchInitData,
    fetchInitDataSuccess,
    fetchInitDataFailure,
    createSdk,
    createSdkSuccess,
    createSdkFailure,
    updateSdk,
    updateSdkSuccess,
    updateSdkFailure,
    deleteSdk,
    deleteSdkSuccess,
    deleteSdkFailure,
    setPage,
    setPageSize,
    fetchSdkLogs,
    fetchSdkLogsSuccess,
    fetchSdkLogsFailure,
    setLogsPage,
    setLogsPageSize,
    setLogsFilters,
    clearLogsFilters,
    clearError,
    clearSuccessMessage,
} = vneidSdkSlice.actions;

// Selectors
export const selectSdkList = (state) => state.vneidSdk?.sdkList || [];
export const selectTotal = (state) => state.vneidSdk?.total || 0;
export const selectPage = (state) => state.vneidSdk?.page || 1;
export const selectPageSize = (state) => state.vneidSdk?.pageSize || 10;
export const selectInitData = (state) => state.vneidSdk?.initData || initialState.initData;
export const selectLoading = (state) => state.vneidSdk?.loading || false;
export const selectListLoading = (state) => state.vneidSdk?.listLoading || false;
export const selectCreateLoading = (state) => state.vneidSdk?.createLoading || false;
export const selectUpdateLoading = (state) => state.vneidSdk?.updateLoading || false;
export const selectDeleteLoading = (state) => state.vneidSdk?.deleteLoading || false;
export const selectError = (state) => state.vneidSdk?.error;
export const selectSuccessMessage = (state) => state.vneidSdk?.successMessage;

// SDK Logs Selectors
export const selectSdkLogs = (state) => state.vneidSdk?.logs || [];
export const selectLogsTotal = (state) => state.vneidSdk?.logsTotal || 0;
export const selectLogsPage = (state) => state.vneidSdk?.logsPage || 1;
export const selectLogsPageSize = (state) => state.vneidSdk?.logsPageSize || 10;
export const selectLogsFilters = (state) => state.vneidSdk?.logsFilters || initialState.logsFilters;
export const selectLogsLoading = (state) => state.vneidSdk?.logsLoading || false;

export default vneidSdkSlice.reducer;
